import { useState, type FormEvent, type JSX } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { Copy, Plus, Webhook } from 'lucide-react';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from '@caratuva/ui';
import type {
  CreateWebhookSubscriptionInput,
  WebhookSubscriptionWithSecretDTO,
} from '@caratuva/shared-types';
import {
  OUTBOUND_WEBHOOK_EVENT_TYPES,
  createWebhookSubscription,
  listWebhookSubscriptions,
} from '../lib/api';

export const Route = createFileRoute('/_app/developers/webhooks/')({
  component: WebhooksIndex,
});

function WebhooksIndex(): JSX.Element {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [revealed, setRevealed] = useState<WebhookSubscriptionWithSecretDTO | null>(null);

  const query = useQuery({
    queryKey: ['webhook-subscriptions'],
    queryFn: ({ signal }) => listWebhookSubscriptions(signal),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-navy-600">
          {intl.formatMessage({
            id: 'developers.webhooks.description',
            defaultMessage:
              'Subscribe an HTTPS endpoint to receive invoice lifecycle events. Each delivery is signed with HMAC-SHA256.',
          })}
        </p>
        <Button type="button" onClick={() => setCreateOpen(true)}>
          <Plus aria-hidden className="h-4 w-4" strokeWidth={1.5} />
          {intl.formatMessage({
            id: 'developers.webhooks.create',
            defaultMessage: 'Add endpoint',
          })}
        </Button>
      </div>

      {query.isPending ? (
        <p className="text-sm text-navy-600">
          {intl.formatMessage({
            id: 'developers.webhooks.loading',
            defaultMessage: 'Loading endpoints...',
          })}
        </p>
      ) : query.isError ? (
        <p className="text-sm text-danger">
          {intl.formatMessage({
            id: 'developers.webhooks.error',
            defaultMessage: 'Failed to load webhook endpoints.',
          })}
        </p>
      ) : query.data.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-md border border-dashed border-navy-200 bg-white px-6 py-10 text-center">
          <Webhook aria-hidden className="h-6 w-6 text-navy-500" strokeWidth={1.5} />
          <p className="text-sm text-navy-600">
            {intl.formatMessage({
              id: 'developers.webhooks.empty',
              defaultMessage: 'No webhook endpoints yet.',
            })}
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-md border border-navy-200 bg-white">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  {intl.formatMessage({
                    id: 'developers.webhooks.column.url',
                    defaultMessage: 'Endpoint URL',
                  })}
                </TableHead>
                <TableHead>
                  {intl.formatMessage({
                    id: 'developers.webhooks.column.events',
                    defaultMessage: 'Events',
                  })}
                </TableHead>
                <TableHead>
                  {intl.formatMessage({
                    id: 'developers.webhooks.column.status',
                    defaultMessage: 'Status',
                  })}
                </TableHead>
                <TableHead>
                  {intl.formatMessage({
                    id: 'developers.webhooks.column.created',
                    defaultMessage: 'Created',
                  })}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {query.data.map((sub) => (
                <TableRow key={sub.id}>
                  <TableCell>
                    <Link
                      to="/developers/webhooks/$id"
                      params={{ id: sub.id }}
                      className="font-mono text-xs text-navy-700 hover:underline"
                    >
                      {sub.url}
                    </Link>
                  </TableCell>
                  <TableCell className="text-navy-600">
                    {sub.eventTypes.length === 1
                      ? sub.eventTypes[0]
                      : intl.formatMessage(
                          {
                            id: 'developers.webhooks.eventCount',
                            defaultMessage: '{count} events',
                          },
                          { count: sub.eventTypes.length },
                        )}
                  </TableCell>
                  <TableCell>
                    {sub.active ? (
                      <Badge variant="outline">
                        {intl.formatMessage({
                          id: 'developers.webhooks.status.active',
                          defaultMessage: 'Active',
                        })}
                      </Badge>
                    ) : (
                      <Badge variant="secondary">
                        {intl.formatMessage({
                          id: 'developers.webhooks.status.disabled',
                          defaultMessage: 'Disabled',
                        })}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-navy-600 tabular-nums">
                    {new Date(sub.createdAt).toLocaleDateString()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <CreateWebhookDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(sub) => {
          setCreateOpen(false);
          setRevealed(sub);
          void queryClient.invalidateQueries({ queryKey: ['webhook-subscriptions'] });
        }}
      />

      <RevealSecretDialog
        title={intl.formatMessage({
          id: 'developers.webhooks.reveal.title',
          defaultMessage: 'Save this signing secret',
        })}
        description={intl.formatMessage({
          id: 'developers.webhooks.reveal.description',
          defaultMessage:
            'Use this secret to verify webhook signatures. It will not be shown again.',
        })}
        secret={revealed?.secret ?? null}
        onClose={() => setRevealed(null)}
      />
    </div>
  );
}

interface CreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (sub: WebhookSubscriptionWithSecretDTO) => void;
}

function CreateWebhookDialog({ open, onOpenChange, onCreated }: CreateDialogProps): JSX.Element {
  const intl = useIntl();
  const [url, setUrl] = useState('');
  const [eventTypes, setEventTypes] = useState<Set<string>>(new Set(OUTBOUND_WEBHOOK_EVENT_TYPES));

  const create = useMutation({
    mutationFn: () => {
      const input: CreateWebhookSubscriptionInput = {
        url: url.trim(),
        eventTypes: Array.from(eventTypes) as CreateWebhookSubscriptionInput['eventTypes'],
      };
      return createWebhookSubscription(input);
    },
    onSuccess: (created) => {
      setUrl('');
      setEventTypes(new Set(OUTBOUND_WEBHOOK_EVENT_TYPES));
      onCreated(created);
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });

  function toggleEvent(name: string): void {
    const next = new Set(eventTypes);
    if (next.has(name)) {
      next.delete(name);
    } else {
      next.add(name);
    }
    setEventTypes(next);
  }

  function selectAll(): void {
    setEventTypes(new Set(OUTBOUND_WEBHOOK_EVENT_TYPES));
  }

  function selectNone(): void {
    setEventTypes(new Set());
  }

  function onSubmit(e: FormEvent): void {
    e.preventDefault();
    if (!url.trim() || eventTypes.size === 0 || create.isPending) return;
    create.mutate();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {intl.formatMessage({
              id: 'developers.webhooks.dialog.title',
              defaultMessage: 'Add webhook endpoint',
            })}
          </DialogTitle>
          <DialogDescription>
            {intl.formatMessage({
              id: 'developers.webhooks.dialog.description',
              defaultMessage:
                'POSTs will be sent to this URL when the selected events fire. The signing secret is shown once on creation.',
            })}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="webhook-url">
              {intl.formatMessage({
                id: 'developers.webhooks.dialog.url',
                defaultMessage: 'Endpoint URL',
              })}
            </Label>
            <Input
              id="webhook-url"
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com/webhooks/caratuva"
              autoFocus
              required
            />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>
                {intl.formatMessage({
                  id: 'developers.webhooks.dialog.events',
                  defaultMessage: 'Events to subscribe to',
                })}
              </Label>
              <div className="flex gap-2 text-xs">
                <button type="button" onClick={selectAll} className="text-navy-700 hover:underline">
                  {intl.formatMessage({
                    id: 'developers.webhooks.dialog.selectAll',
                    defaultMessage: 'Select all',
                  })}
                </button>
                <span className="text-navy-300">·</span>
                <button
                  type="button"
                  onClick={selectNone}
                  className="text-navy-700 hover:underline"
                >
                  {intl.formatMessage({
                    id: 'developers.webhooks.dialog.selectNone',
                    defaultMessage: 'None',
                  })}
                </button>
              </div>
            </div>
            <div className="grid max-h-64 grid-cols-1 gap-1 overflow-y-auto rounded-md border border-navy-200 p-2">
              {OUTBOUND_WEBHOOK_EVENT_TYPES.map((event) => (
                <label
                  key={event}
                  className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-navy-50"
                >
                  <input
                    type="checkbox"
                    checked={eventTypes.has(event)}
                    onChange={() => toggleEvent(event)}
                    className="h-4 w-4 rounded border-navy-300"
                  />
                  <span className="font-mono text-xs text-navy-700">{event}</span>
                </label>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {intl.formatMessage({ id: 'common.cancel', defaultMessage: 'Cancel' })}
            </Button>
            <Button
              type="submit"
              disabled={create.isPending || !url.trim() || eventTypes.size === 0}
            >
              {create.isPending
                ? intl.formatMessage({
                    id: 'developers.webhooks.dialog.submitting',
                    defaultMessage: 'Creating...',
                  })
                : intl.formatMessage({
                    id: 'developers.webhooks.dialog.submit',
                    defaultMessage: 'Add endpoint',
                  })}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface RevealSecretDialogProps {
  title: string;
  description: string;
  secret: string | null;
  onClose: () => void;
}

function RevealSecretDialog({
  title,
  description,
  secret,
  onClose,
}: RevealSecretDialogProps): JSX.Element {
  const intl = useIntl();

  function copy(): void {
    if (!secret) return;
    void navigator.clipboard.writeText(secret);
    toast.success(
      intl.formatMessage({
        id: 'developers.copied',
        defaultMessage: 'Copied to clipboard.',
      }),
    );
  }

  return (
    <Dialog open={secret !== null} onOpenChange={(o) => (!o ? onClose() : undefined)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {secret ? (
          <div className="flex items-center gap-2 rounded-md border border-navy-200 bg-navy-50 px-3 py-2">
            <code className="flex-1 break-all font-mono text-xs text-navy-900">{secret}</code>
            <button
              type="button"
              onClick={copy}
              className="rounded p-1.5 text-navy-600 hover:bg-white hover:text-navy-900"
              aria-label={intl.formatMessage({
                id: 'developers.copy',
                defaultMessage: 'Copy',
              })}
            >
              <Copy aria-hidden className="h-4 w-4" strokeWidth={1.5} />
            </button>
          </div>
        ) : null}
        <DialogFooter>
          <Button type="button" onClick={onClose}>
            {intl.formatMessage({
              id: 'developers.reveal.done',
              defaultMessage: "I've saved it",
            })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
